/**
 * Auth session store: user, roles, interface access, and logout cleanup.
 */

import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { User } from '@/types/user.types';
import type { UserPreferences } from '@/types/preferences.types';
import { UserRole, InterfaceAccess } from '@/types/permissions.types';
import { usePreferencesStore } from './preferencesStore';
import { useInterfaceElementsStore } from './interfaceElementsStore';
import { useRoleCatalogStore } from './roleCatalogStore';

/** localStorage key for the persisted staff session snapshot. */
const AUTH_STORE_KEY = 'ednov-auth';

/** Persist writes a logged-out snapshot only after an intentional logout. */
const authPersistStorage: StateStorage = {
  getItem: (name) => localStorage.getItem(name),
  setItem: (name, value) => {
    try {
      const parsed = JSON.parse(value) as { state?: { user?: User | null } };
      if (parsed.state?.user == null && localStorage.getItem('intentional_logout') !== 'true') {
        return;
      }
    } catch {
      /* keep writing — malformed payloads still replace the snapshot */
    }
    localStorage.setItem(name, value);
  },
  removeItem: (name) => localStorage.removeItem(name),
};

/** Fields written to {@link AUTH_STORE_KEY} so a refresh can skip GET /users/login. */
type PersistedAuthState = {
  user: User | null;
  roles: UserRole[];
  interfaceAccess: Record<string, InterfaceAccess>;
  isAuthenticated: boolean;
};

/** Zustand auth session: user, roles, RBAC access, and logout cleanup. */
interface AuthStore {
  user: User | null;
  roles: UserRole[];
  interfaceAccess: Record<string, InterfaceAccess>;
  preferences: UserPreferences | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;

  /** Set the current user and flip {@link AuthStore.isAuthenticated}. */
  setUser: (_user: User | null) => void;
  setRoles: (_roles: UserRole[]) => void;
  setInterfaceAccess: (_access: Record<string, InterfaceAccess>) => void;
  setPreferences: (_preferences: UserPreferences | null) => void;
  /** Hydrate user + roles + access after login; ends bootstrapping. */
  setAuthData: (
    _user: User,
    _roles: UserRole[],
    _interfaceAccess?: Record<string, InterfaceAccess>,
  ) => void;
  setBootstrapping: (_isBootstrapping: boolean) => void;

  /** True when the interface has at least one loaded RBAC element. */
  canViewInterface: (_interfaceName: string) => boolean;
  /** Same presence check as {@link AuthStore.canViewInterface} (no edit/view split). */
  canEditInterface: (_interfaceName: string) => boolean;
  canViewElement: (_interfaceName: string, _elementId: string) => boolean;
  canEditElement: (_interfaceName: string, _elementId: string) => boolean;
  /** Sidebar menu visibility from {@link AuthStore.interfaceAccess}.sidebar. */
  canViewMenu: (_menuId: string) => boolean;
  /** Clear session + persisted preferences / interface-elements caches. */
  logout: () => void;
}

/** Session store used by {@link AuthProvider} and permission hooks. */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      roles: [],
      interfaceAccess: {},
      preferences: null,
      isAuthenticated: false,
      isBootstrapping: true,

      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
      },

      setRoles: (roles) => {
        set({ roles });
      },

      setInterfaceAccess: (access) => {
        set({ interfaceAccess: access });
      },

      setPreferences: (preferences) => {
        set({ preferences });
      },

      setAuthData: (user, roles, interfaceAccess = {}) => {
        set({
          user,
          roles,
          interfaceAccess,
          isAuthenticated: true,
          isBootstrapping: false,
        });
      },

      setBootstrapping: (isBootstrapping) => {
        set({ isBootstrapping });
      },

      canViewInterface: (interfaceName: string) => {
        // Presence model: any loaded elements mean the user can access the interface.
        const elements = useInterfaceElementsStore.getState().elementsByInterface[interfaceName];
        return (elements?.length ?? 0) > 0;
      },

      canEditInterface: (interfaceName: string) => {
        // Presence model: same as canViewInterface (no edit/view distinction).
        const elements = useInterfaceElementsStore.getState().elementsByInterface[interfaceName];
        return (elements?.length ?? 0) > 0;
      },

      canViewElement: (interfaceName: string, elementId: string) => {
        const { interfaceAccess } = get();
        const access = interfaceAccess[interfaceName];
        return access?.elements?.[elementId]?.visible ?? false;
      },

      canEditElement: (interfaceName: string, elementId: string) => {
        const { interfaceAccess } = get();
        const access = interfaceAccess[interfaceName];
        return access?.elements?.[elementId]?.editable ?? false;
      },

      canViewMenu: (menuId: string) => {
        const { interfaceAccess } = get();
        const sidebarAccess = interfaceAccess['sidebar'];
        if (!sidebarAccess) return false;

        const menuElement = sidebarAccess.elements?.[menuId];
        return menuElement?.visible ?? false;
      },

      logout: () => {
        set({
          user: null,
          roles: [],
          interfaceAccess: {},
          preferences: null,
          isAuthenticated: false,
          isBootstrapping: false,
        });

        usePreferencesStore.getState().clearPreferences();
        useInterfaceElementsStore.getState().clearElements();
        useRoleCatalogStore.getState().clear();

        try {
          localStorage.removeItem('ednov-preferences');
          localStorage.removeItem('interface-elements-store');
          useAuthStore.persist.clearStorage();
        } catch (e) {
          console.error('Failed to clear storage:', e);
        }
      },
    }),
    {
      name: AUTH_STORE_KEY,
      storage: createJSONStorage(() => authPersistStorage),
      partialize: (state): PersistedAuthState => ({
        user: state.user,
        roles: state.roles,
        interfaceAccess: state.interfaceAccess,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
